import type { ExerciseKind } from '@echo/core';
import { todayIso, useMyProgress, useMyStats, useToday } from '@echo/core/react';
import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { DailyLogCard } from '@/components/daily-log-card';
import { OverviewCard } from '@/components/overview-card';
import { CompletionCard, WeekChartCard } from '@/components/progress-cards';
import { exerciseOpen } from '@/lib/exercise-routes';

export const Route = createFileRoute('/_app/progress')({ component: ProgressPage });

/** Overview, the daily log, exercises per week (Mon → Sun) and completion by exercise. */
function ProgressPage() {
  const { t } = useLingui();
  const stats = useMyStats();
  // The day picked in the log's date selector; the log ends on today until one is.
  const [logDay, setLogDay] = useState<string>();
  const progress = useMyProgress(logDay);
  const today = useToday();
  const exercisesThisWeek = progress.data?.exercisesThisWeek ?? 0;
  const nextKind = progress.data?.nextKind;
  // Today's rows know what is under way, so the log opens an exercise the way Home does.
  const statusOf = (kind: ExerciseKind) =>
    today.data?.exercises.find((exercise) => exercise.kind === kind)?.status ?? 'todo';

  return (
    <>
      <h1 className="text-display-sm font-bold">
        <Trans>Progress</Trans>
      </h1>
      <div className="flex flex-col gap-section-sm">
        <OverviewCard stats={stats.data} />

        <DailyLogCard
          log={progress.data?.log ?? []}
          nextKind={nextKind}
          startNext={({ className, children }) =>
            nextKind ? (
              <Link {...exerciseOpen(nextKind, statusOf(nextKind))} className={className}>
                {children}
              </Link>
            ) : null
          }
          openExercise={(kind, { className, children, ...props }) => (
            <Link
              key={kind}
              {...exerciseOpen(kind, statusOf(kind))}
              className={className}
              {...props}
            >
              {children}
            </Link>
          )}
          dates={
            progress.data
              ? {
                  value: logDay ?? todayIso(),
                  first: progress.data.firstDate,
                  last: todayIso(),
                  onChange: setLogDay,
                }
              : undefined
          }
        />

        <WeekChartCard
          title={t`Exercises per week`}
          subtitle={plural(exercisesThisWeek, {
            one: 'You completed # exercise this week.',
            other: 'You completed # exercises this week.',
          })}
          week={progress.data?.week ?? []}
        />

        <CompletionCard
          subtitle={t`Your average completion rate this week.`}
          rates={progress.data?.completionByKind}
        />
      </div>
    </>
  );
}
